/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import Image from "next/image";
import { useState, FormEvent, useEffect, useRef } from "react";
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartEvent,
  ActiveElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAuth } from "./contexts/AuthContext";
import { Fragment } from "react";
import React from "react";
import QRCodeGenerator from "./QRCodeGenerator";
import UsuariosCRUD from "./UsuariosCRUD";
import OSGrade from "./OSGrade";
import OSModal from "./OSModal";
import DepartamentoCRUD from "./DepartamentoCRUD";
import type { TooltipItem } from 'chart.js';
import type { Context } from 'chartjs-plugin-datalabels';
import { Loader } from './components/Loader';
import OSModalNew from "./OSModalNew";
import OSMobileView from "./OSMobileView";
import { ENDPOINTS } from "./endpoints";
import { temPermissao } from "./utils/permissoes";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const unidades = ["IACS", "IAESC", "FAAMA", "ADVSERV"];
const imagens = [
  "/assets/1_login.jpg",
  "/assets/2_login.jpg",
  "/assets/3_login.jpg",
  "/assets/4_login.jpg",
  "/assets/5_login.jpg",
];

function getRandomImage() {
  const idx = Math.floor(Math.random() * imagens.length);
  return imagens[idx];
}

// Adiciona o CDN do Font Awesome no head
function useFontAwesome() {
  useEffect(() => {
    if (!document.getElementById("fa-cdn")) {
      const link = document.createElement("link");
      link.id = "fa-cdn";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css";
      document.head.appendChild(link);
    }
  }, []);
}

type ModalErroProps = {
  open: boolean;
  onClose: () => void;
};

function ModalErro({ open, onClose }: ModalErroProps) {
  if (!open) return null;
  return (
    <>
      <style>{`
        .modal-anim {
          animation: zoomInFade 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes zoomInFade {
          0% {
            opacity: 0;
            transform: scale(0.7);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
        <div className="modal-anim bg-white rounded-lg shadow-lg p-8 flex flex-col items-center max-w-[400px] w-full">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" className="mb-4">
            <circle cx="40" cy="40" r="38" stroke="#f87171" strokeWidth="4" fill="#fff" />
            <path d="M28 28L52 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
            <path d="M52 28L28 52" stroke="#f87171" strokeWidth="4" strokeLinecap="round" />
          </svg>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">ERRO</h2>
          <p className="text-gray-600 mb-6 text-center">Usuário ou Senha incorretos!</p>
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
}

function Avatar() {
  // SVG de avatar simples
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="32" fill="#e5e7eb" />
      <circle cx="32" cy="26" r="14" fill="#a3a3a3" />
      <ellipse cx="32" cy="50" rx="18" ry="10" fill="#a3a3a3" />
    </svg>
  );
}

// Hook para detectar largura da tela
function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

function getLegendaColunas(qtd: number, larguraTela: number) {
  if (larguraTela < 600) return 2;
  if (qtd <= 15) return 1;
  if (qtd <= 30) return 2;
  if (qtd <= 45) return 3;
  if (qtd <= 60) return 4;
  return 5;
}

// Plugin customizado para desenhar labels radialmente
const radialLabelPlugin = {
  id: 'radialLabelPlugin',
  afterDraw(chart: any) {
    const { ctx, chartArea, data } = chart;
    if (!chartArea) return;
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0].data as number[];
    const total = dataset.reduce((a: number, b: number) => a + b, 0);
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    const radius = Math.min((chartArea.right - chartArea.left), (chartArea.bottom - chartArea.top)) / 2 * 0.85;
    let startAngle = -0.5 * Math.PI;
    for (let i = 0; i < dataset.length; i++) {
      const value = dataset[i];
      const percent = (value / total) * 100;
      if (percent < 30) {
        startAngle += (value / total) * 2 * Math.PI;
        continue;
      }
      const label = data.labels[i];
      const angle = (value / total) * 2 * Math.PI;
      const midAngle = startAngle + angle / 2;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(midAngle);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold 13px Arial';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 2;
      ctx.fillText(`${label} (${percent.toFixed(1)}%)`, radius * 0.1, 0);
      ctx.restore();
      startAngle += angle;
    }
  }
};

// Componente de Abas Personalizadas
function AbasNavegacao({ abas, abaAtiva, setAbaAtiva, fecharAba }: { abas: { id: string, titulo: string, icone: string }[], abaAtiva: string, setAbaAtiva: (id: string) => void, fecharAba: (id: string) => void }) {
  return (
    <div className="w-full flex justify-center bg-gray-100 py-2 shadow z-30">
      <div className="flex items-center gap-2">
        {/* Aba Home */}
        <button
          className={`rounded-full p-2 transition-colors duration-200 shadow ${abaAtiva === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-blue-100'}`}
          onClick={() => setAbaAtiva('dashboard')}
          title="Dashboard"
        >
          <i className="fa fa-home"></i>
        </button>
        {/* Outras abas */}
        {abas.filter(aba => aba.id !== 'dashboard').map(aba => (
          <div
            key={aba.id}
            className={`flex items-center rounded-full px-4 py-2 mx-1 shadow transition-colors duration-200 cursor-pointer select-none ${abaAtiva === aba.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-blue-100'}`}
            style={{ minWidth: 90 }}
          >
            {aba.icone && <i className={`fa fa-${aba.icone} mr-2`}></i>}
            <span onClick={() => setAbaAtiva(aba.id)}>{aba.titulo}</span>
            <button
              onClick={() => fecharAba(aba.id)}
              className="ml-2 text-lg hover:text-red-500 focus:outline-none bg-transparent border-0 cursor-pointer"
              style={{ lineHeight: 1 }}
              title="Fechar aba"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ usuario, unidade, onLogout }: { usuario: string; unidade: string; onLogout: () => void }) {
  useFontAwesome();
  const { loginGrupoId, loginLoginId, loginDepartamentoId, setLoginUnidade, setLoginUsuario, setLoginSenha, setLoginGrupoNome, setLoginLoginNome, setPermissoes, loginLoginNome, loginUnidade, permissoes } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [departamentos, setDepartamentos] = useState<{ descricao_departamento: string; quantidade: number }[]>([]);
  const [loadingPizza, setLoadingPizza] = useState(true);
  // Cores dos cards (não podem ser usadas na pizza)
  const coresCards = [
    '#444950', // aguardando
    '#f7a12a', // distribuídas
    '#2ea7df', // andamento
    '#1dbbb4', // concluídas
    '#e05a5a', // canceladas
  ];

  // Paleta de cores para a pizza/legenda, removendo as cores dos cards
  const coresPizza = [
    '#2360a8', // azul escuro
    '#2ec4b6', // verde água
    '#a82323', // vermelho escuro
    '#f4b400', // amarelo
    '#888',    // cinza
    '#c44d2d', // laranja queimado
    '#6c3483', // roxo
    '#ff7f50', // coral
    '#16a085', // teal
    '#34495e', // azul petróleo
    '#f39c12', // amarelo escuro
    '#27ae60', // verde escuro
    '#e67e22', // laranja escuro
    '#2980b9', // azul médio
    '#8e44ad', // roxo escuro
    '#d35400', // laranja forte
    '#7f8c8d', // cinza médio
    '#b9770e', // mostarda
    '#45b39d', // verde água escuro
    '#e74c3c', // vermelho vibrante
    '#5dade2', // azul bebê
    '#52be80', // verde folha
    '#f1948a', // rosa claro
    '#bb8fce', // lilás
    '#f5b041', // amarelo pastel
    '#85929e', // cinza azulado
  ].filter(cor => !coresCards.includes(cor));
  const windowWidth = useWindowWidth();
  const [contadores, setContadores] = useState({
    aguardando: '0',
    distribuidas: '0',
    andamento: '0',
    concluidas: '0',
    canceladas: '0',
  });
  const [tela, setTela] = useState<'dashboard' | 'departamentos' | 'qrcode' | 'usuarios' | 'os'>('dashboard');
  const [modalOSAberto, setModalOSAberto] = useState(false);
  const [filtroStatusOS, setFiltroStatusOS] = useState<string | null>(null);
  const [filtroDepartamentoOS, setFiltroDepartamentoOS] = useState<string | null>(null);
  // Estado para abas abertas
  const [abas, setAbas] = useState([
    { id: 'dashboard', titulo: '', icone: 'home' as string },
  ]);
  const [abaAtiva, setAbaAtiva] = useState('dashboard');
  const [handleGerarPDF, setHandleGerarPDF] = useState<(() => void) | null>(null);
  // Ref para acessar a função gerarPDF do OSGrade
  const OSGradeRef = useRef<{ gerarPDF: () => void; getContadoresFiltrados: () => any; filtros: any }>(null);
  const [filtrosMudaram, setFiltrosMudaram] = useState(0);
  const versao = useAppVersion();
  const podeCriar = temPermissao(permissoes, "os", "criar_os");

  async function fetchDepartamentos() {
    setLoadingPizza(true);
    try {
      const payload: any = { unidade: unidade.toLowerCase() };
      // Se for executor (grupo_id = 4), inclui o usuario_os como número
      if (loginGrupoId === "4") {
        payload.usuario_os = parseInt(loginLoginId);
      }
      console.log('Payload Departamentos:', payload);
      const response = await fetch(ENDPOINTS.DASHBOARD_DEPARTAMENTOS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      setDepartamentos(Array.isArray(data) ? data : []);
    } catch (err) {
      setDepartamentos([]);
    }
    setLoadingPizza(false);
  }

  async function fetchContadores() {
    try {
      const payload: any = { unidade: unidade.toLowerCase() };
      // Se for executor (grupo_id = 4), inclui o usuario_os como número
      if (loginGrupoId === "4") {
        payload.usuario_os = parseInt(loginLoginId);
      }
      console.log('Payload Contadores:', payload);
      const response = await fetch(ENDPOINTS.DASHBOARD_CONTADORES, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setContadores(data[0]);
      }
    } catch (err) {
      // Se der erro, mantém os valores zerados
    }
  }

  useEffect(() => {
    fetchDepartamentos();
  }, [unidade]);

  useEffect(() => {
    fetchContadores();
  }, [unidade]);

  const total = departamentos.reduce((acc, d) => acc + d.quantidade, 0) || 1;
  const pieData = {
    labels: departamentos.map(d => d.descricao_departamento),
    datasets: [
      {
        data: departamentos.map(d => d.quantidade),
        backgroundColor: departamentos.map((_, i) => coresPizza[i % coresPizza.length]),
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };
  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          title: () => '',
          label: function(context: TooltipItem<'pie'>) {
            const dataset = context.dataset.data as number[];
            const total = dataset.reduce((a, b) => a + b, 0);
            const value = context.dataset.data[context.dataIndex];
            const label = context.chart.data.labels?.[context.dataIndex] || '';
            const percent = ((value / total) * 100).toFixed(1);
            return `${label} (${percent}%)`;
          },
        }
      },
      datalabels: { display: false },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
    },
    elements: {
      arc: {
        hoverOffset: 16,
      },
    },
    onHover: (event: ChartEvent, elements: ActiveElement[], chart: any) => {
      if (event && event.native) {
        const target = event.native.target as HTMLElement;
        target.style.cursor = elements.length ? 'pointer' : 'default';
      }
    },
    onClick: (event: ChartEvent, elements: ActiveElement[], chart: any) => {
      if (elements && elements.length > 0) {
        const idx = elements[0].index;
        const departamento = chart.data.labels[idx];
        setFiltroDepartamentoOS(departamento);
        setTela('os');
      }
    },
  };

  // Paleta de cores fixa para os cards (contadores), inspirada no topo, mas exclusiva
  // Não usar nenhuma dessas na pizza
  const cards = [
    { label: "Aguardando", value: contadores.aguardando, color: "bg-[#444950] text-white", icon: "fa-hourglass-end" },
    { label: "Distribuídas", value: contadores.distribuidas, color: "bg-[#f7a12a] text-white", icon: "fa-user" },
    { label: "Andamento", value: contadores.andamento, color: "bg-[#2ea7df] text-white", icon: "fa-wrench" },
    { label: "Concluídas", value: contadores.concluidas, color: "bg-[#1dbbb4] text-white", icon: "fa-flag-checkered" },
    { label: "Canceladas", value: contadores.canceladas, color: "bg-[#e05a5a] text-white", icon: "fa-times" },
  ];

  // Mapeamento dos nomes dos cards para o valor do filtro de status
  const statusMap: Record<string, string> = {
    'Aguardando': 'AGUARDANDO',
    'Distribuídas': 'DISTRIBUIDA',
    'Andamento': 'ACEITA',
    'Concluídas': 'CONCLUIDA',
    'Canceladas': 'CANCELADA',
  };

  // Função para ativar uma aba e mostrar a tela correspondente
  function ativarAba(id: string) {
    setAbaAtiva(id);
    setTela(id === 'dashboard' ? 'dashboard' : id as any);
  }

  // Função para fechar uma aba e navegar para a anterior ou dashboard
  function fecharAba(id: string) {
    setAbas(abs => {
      const idx = abs.findIndex(a => a.id === id);
      const novasAbas = abs.filter(a => a.id !== id);
      if (abaAtiva === id) {
        // Se fechou a aba ativa, ativa a anterior ou dashboard
        const novaAba = novasAbas[idx - 1] || novasAbas[0] || { id: 'dashboard' };
        setAbaAtiva(novaAba.id);
        setTela(novaAba.id === 'dashboard' ? 'dashboard' : novaAba.id as any);
      }
      return novasAbas;
    });
  }

  // Efeito para garantir que a aba correta está aberta e ativa ao mudar de tela
  useEffect(() => {
    if (tela === 'departamentos') {
      if (!abas.find(a => a.id === 'departamentos')) {
        setAbas(abs => [...abs, { id: 'departamentos', titulo: 'Departamentos', icone: 'building' }]);
      }
      if (abaAtiva !== 'departamentos') setAbaAtiva('departamentos');
    } else if (tela === 'qrcode') {
      if (!abas.find(a => a.id === 'qrcode')) {
        setAbas(abs => [...abs, { id: 'qrcode', titulo: 'QRCode', icone: 'qrcode' }]);
      }
      if (abaAtiva !== 'qrcode') setAbaAtiva('qrcode');
    } else if (tela === 'usuarios') {
      if (!abas.find(a => a.id === 'usuarios')) {
        setAbas(abs => [...abs, { id: 'usuarios', titulo: 'Usuários', icone: 'users' }]);
      }
      if (abaAtiva !== 'usuarios') setAbaAtiva('usuarios');
    } else if (tela === 'os') {
      if (!abas.find(a => a.id === 'os')) {
        setAbas(abs => [...abs, { id: 'os', titulo: 'OS', icone: 'list-check' }]);
      }
      if (abaAtiva !== 'os') setAbaAtiva('os');
    } else if (tela === 'dashboard') {
      if (abaAtiva !== 'dashboard') setAbaAtiva('dashboard');
    }
  }, [tela, abas, abaAtiva]);

  // Só registra a função de gerar PDF quando a tela for 'os'
  useEffect(() => {
    if (tela !== 'os') setHandleGerarPDF(null);
  }, [tela]);

  // Renderização principal
  const renderMenuLateral = (
    <>
      {/* Menu lateral (drawer) */}
      <div
        className={`fixed top-0 left-0 h-full w-[270px] bg-[#162544] text-white z-50 shadow-lg transform transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ willChange: "transform" }}
      >
        <div className="flex flex-col items-center py-6">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-2">
            <Avatar />
          </div>
          <div className="font-bold text-lg">{(usuario || '').toUpperCase()}</div>
          <div className="text-xs mb-4">({unidade || ''})</div>
        </div>
        <nav className="flex flex-col gap-2 px-6">
          <button onClick={() => { setTela('departamentos'); setMenuOpen(false); }} className="flex items-center gap-3 py-2 hover:bg-white/10 rounded text-left">
            <i className="fa fa-building"></i>
            Departamentos
          </button>
          <button onClick={() => { setTela('qrcode'); setMenuOpen(false); }} className="flex items-center gap-3 py-2 hover:bg-white/10 rounded text-left">
            <i className="fa fa-qrcode"></i>
            QRCode
          </button>
          <button onClick={() => { setTela('usuarios'); setMenuOpen(false); }} className="flex items-center gap-3 py-2 hover:bg-white/10 rounded text-left">
            <i className="fa fa-users"></i>
            Usuários
          </button>
          <button onClick={() => { setFiltroDepartamentoOS(null); setFiltroStatusOS(null); setTela('os'); setMenuOpen(false); }} className="flex items-center gap-3 py-2 hover:bg-white/10 rounded text-left">
            <i className="fa fa-list-check"></i>
            OS
          </button>
        </nav>
        <button
          className="absolute top-4 right-4 text-2xl text-white/70 hover:text-white focus:outline-none cursor-pointer"
          onClick={() => setMenuOpen(false)}
          aria-label="Fechar menu"
        >
          <i className="fa fa-xmark"></i>
        </button>
      </div>
      {/* Overlay para fechar o menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );

  // Renderização das abas mantendo o estado
  if (["dashboard", "departamentos", "qrcode", "usuarios", "os"].includes(tela)) {
    return (
      <div className="min-h-screen bg-[#f5f6f8] font-sans">
        {/* Topo */}
        <div className="bg-[#1a2940] text-white flex items-center justify-between px-6 py-2">
          <div className="flex items-center gap-4">
            <button onClick={() => setMenuOpen(true)} className="text-2xl focus:outline-none mr-2 cursor-pointer" title="Menu">
              <i className="fa fa-bars"></i>
            </button>
            <div className="flex flex-col items-center justify-center min-w-[110px]">
              <span className="font-bold tracking-wide text-lg leading-tight">ADVSERV</span>
              <span className="text-xs font-normal text-gray-300 mt-[-2px] text-center w-full">{versao}</span>
            </div>
            <span className="ml-6 text-sm">Usuário: <b>{(usuario || '').toUpperCase()}</b> - Empresa: <b>{unidade || ''}</b></span>
          </div>
          <div className="flex gap-4 items-center text-lg">
            <button onClick={onLogout} className="focus:outline-none cursor-pointer" title="Logout">
              <i className="fa fa-power-off"></i>
            </button>
          </div>
        </div>
        {/* Abas Navegação */}
        <AbasNavegacao abas={abas} abaAtiva={abaAtiva} setAbaAtiva={ativarAba} fecharAba={fecharAba} />
        {/* Todas as abas montadas, só uma visível */}
        <div style={{ display: tela === 'dashboard' ? 'block' : 'none' }}>
          {/* Cards */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
            {cards.map((card, i) => (
              <div
                key={i}
                className={`rounded-lg px-6 py-4 flex flex-col justify-between h-24 shadow ${card.color} relative cursor-pointer hover:brightness-95 transition`}
                onClick={() => { setFiltroStatusOS(statusMap[card.label] || card.label.toUpperCase()); setFiltroDepartamentoOS(null); setTela('os'); }}
                style={{ cursor: 'pointer' }}
                title={`Ver OS com status ${card.label}`}
              >
                <span className="text-3xl font-bold leading-none">{card.value}</span>
                <span className="text-xs mt-2 opacity-80">{card.label}</span>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-3xl opacity-30"><i className={`fa ${card.icon}`}></i></span>
              </div>
            ))}
          </div>
          {/* Gráfico de pizza Chart.js e botão Atualizar */}
          <div className="flex flex-col md:flex-row gap-4 p-4 w-full min-h-[420px]" style={{paddingLeft: 32, paddingRight: 32}}>
            <div className="bg-white rounded-lg shadow p-4 flex-1 flex flex-col h-[70vh] min-h-[420px] relative">
              {/* Botão Atualizar no canto superior direito do gráfico */}
              <button
                className="absolute right-6 top-6 bg-blue-600 hover:bg-blue-700 text-white rounded px-5 py-2 flex items-center gap-2 shadow font-bold z-10 cursor-pointer"
                title="Atualizar dados do dashboard"
                onClick={() => { fetchDepartamentos(); fetchContadores(); }}
              >
                <i className="fa fa-rotate text-xl"></i>
                Atualizar
              </button>
              <div className="w-full h-full flex items-center justify-center">
                {loadingPizza ? (
                  <span className="text-gray-400">Carregando gráfico...</span>
                ) : (
                  <Pie data={pieData} options={pieOptions} style={{ width: '100%', height: '100%' }} />
                )}
              </div>
            </div>
            {/* Legenda dinâmica */}
            <div className="bg-white rounded-lg shadow p-4 min-w-[260px] flex flex-col h-[70vh] min-h-[420px]" style={{ overflowY: 'auto' }}>
              <div className="font-bold mb-2 text-base text-black">Legenda</div>
              <ul
                className="text-xs gap-x-6 gap-y-2 flex-1"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${getLegendaColunas(departamentos.length, windowWidth)}, minmax(0, 1fr))`,
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                }}
              >
                {departamentos.map((dep, i) => (
                  <li
                    key={dep.descricao_departamento}
                    className="flex items-center gap-2 mb-1 cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    title={`Filtrar por ${dep.descricao_departamento}`}
                    onClick={() => { setFiltroDepartamentoOS(dep.descricao_departamento); setFiltroStatusOS(null); setTela('os'); }}
                  >
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: coresPizza[i % coresPizza.length] }}></span>
                    <span className="font-semibold" style={{color: coresPizza[i % coresPizza.length]}}>{dep.descricao_departamento}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div style={{ display: tela === 'departamentos' ? 'block' : 'none' }}>
          <DepartamentoCRUD />
        </div>
        <div style={{ display: tela === 'qrcode' ? 'block' : 'none' }}>
          <QRCodeGenerator />
        </div>
        <div style={{ display: tela === 'usuarios' ? 'block' : 'none' }}>
          <UsuariosCRUD />
        </div>
        <div style={{ display: tela === 'os' ? 'block' : 'none' }}>
          {/* Linha de botões de ação e contadores + grade de OS */}
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2 px-6 mt-6">
            <div className="flex gap-2">
              {podeCriar && (
                <button className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 transition-colors cursor-pointer" onClick={() => setModalOSAberto(true)}>
                  <i className="fa fa-plus mr-2"></i>Nova OS
                </button>
              )}
              <button className="bg-gray-700 text-white px-4 py-2 rounded font-semibold hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => OSGradeRef.current && OSGradeRef.current.gerarPDF()}>
                <i className="fa fa-print mr-2"></i>Imprimir Listagem
              </button>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <div className={`flex items-center gap-2 bg-gray-200 text-gray-900 px-4 py-2 rounded shadow font-semibold transition-all duration-200 ${filtroStatusOS === 'AGUARDANDO' ? 'ring-2 ring-blue-500' : ''} cursor-pointer`} onClick={() => { setFiltroStatusOS(filtroStatusOS === 'AGUARDANDO' ? null : 'AGUARDANDO'); setFiltroDepartamentoOS(null); }} title="Filtrar por Aguardando">
                <i className="fa fa-hourglass-end"></i>
                Aguardando <span className="ml-1 font-bold">{contadores.aguardando}</span>
              </div>
              <div className={`flex items-center gap-2 bg-yellow-400 text-gray-900 px-4 py-2 rounded shadow font-semibold transition-all duration-200 ${filtroStatusOS === 'DISTRIBUIDA' ? 'ring-2 ring-blue-500' : ''} cursor-pointer`} onClick={() => { setFiltroStatusOS(filtroStatusOS === 'DISTRIBUIDA' ? null : 'DISTRIBUIDA'); setFiltroDepartamentoOS(null); }} title="Filtrar por Distribuídas">
                <i className="fa fa-share"></i>
                Distribuídas <span className="ml-1 font-bold">{contadores.distribuidas}</span>
              </div>
              <div className={`flex items-center gap-2 bg-cyan-400 text-white px-4 py-2 rounded shadow font-semibold transition-all duration-200 ${filtroStatusOS === 'ACEITA' ? 'ring-2 ring-blue-500' : ''} cursor-pointer`} onClick={() => { setFiltroStatusOS(filtroStatusOS === 'ACEITA' ? null : 'ACEITA'); setFiltroDepartamentoOS(null); }} title="Filtrar por Aceitas">
                <i className="fa fa-check-double"></i>
                Aceitas <span className="ml-1 font-bold">{contadores.andamento}</span>
              </div>
              <div className={`flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded shadow font-semibold transition-all duration-200 ${filtroStatusOS === 'CONCLUIDA' ? 'ring-2 ring-blue-500' : ''} cursor-pointer`} onClick={() => { setFiltroStatusOS(filtroStatusOS === 'CONCLUIDA' ? null : 'CONCLUIDA'); setFiltroDepartamentoOS(null); }} title="Filtrar por Concluídas">
                <i className="fa fa-flag-checkered"></i>
                Concluídas <span className="ml-1 font-bold">{contadores.concluidas}</span>
              </div>
              <div className={`flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded shadow font-semibold transition-all duration-200 ${filtroStatusOS === 'CANCELADA' ? 'ring-2 ring-blue-500' : ''} cursor-pointer`} onClick={() => { setFiltroStatusOS(filtroStatusOS === 'CANCELADA' ? null : 'CANCELADA'); setFiltroDepartamentoOS(null); }} title="Filtrar por Canceladas">
                <i className="fa fa-times"></i>
                Canceladas <span className="ml-1 font-bold">{contadores.canceladas}</span>
              </div>
            </div>
          </div>
          <div className="p-6 pt-0">
            {tela === 'os' && (
              <OSGrade
                ref={OSGradeRef}
                unidade={unidade}
                filtroStatus={filtroStatusOS || undefined}
                filtroDepartamentoInicial={filtroDepartamentoOS || undefined}
                onLimparFiltroStatus={() => setFiltroStatusOS(null)}
                onFiltrosChange={() => setTimeout(() => setFiltrosMudaram(f => f + 1), 0)}
              />
            )}
          </div>
          {modalOSAberto && (
            <OSModalNew 
              onClose={() => setModalOSAberto(false)} 
              unidade={unidade.toLowerCase()}
              onCriada={() => {
                setModalOSAberto(false);
                if (OSGradeRef.current && typeof OSGradeRef.current.fetchOS === 'function') {
                  OSGradeRef.current.fetchOS(true);
                }
              }}
            />
          )}
        </div>
        {renderMenuLateral}
      </div>
    );
  }
}

export default function Home() {
  const [imagemDireita, setImagemDireita] = useState<string | null>(null);
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [logado, setLogado] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setLoginUnidade, setLoginUsuario, setLoginSenha, setLoginGrupoId, setLoginGrupoNome, setLoginLoginId, setLoginLoginNome, setLoginDepartamentoId, loginLoginNome, loginUnidade, setPermissoes } = useAuth();
  const [unidade, setUnidade] = useState(unidades[0]);
  const [usuario, setUsuario] = useState("");
  const [modalOSAberto, setModalOSAberto] = useState(false);
  const versao = useAppVersion();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const ultimaUnidade = localStorage.getItem('ultimaUnidade');
      if (ultimaUnidade) setUnidade(ultimaUnidade);
      const ultimoUsuario = localStorage.getItem('ultimoUsuario');
      if (ultimoUsuario) setUsuario(ultimoUsuario);
    }
  }, []);

  useEffect(() => {
    setImagemDireita(getRandomImage());
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setErro("");
    try {
      // Salva unidade e usuário no localStorage ao fazer login
      localStorage.setItem('ultimaUnidade', unidade);
      localStorage.setItem('ultimoUsuario', usuario);
      const payload = {
        unidade: unidade.toLowerCase().trim(),
        login: usuario.trim(),
        senha: senha.trim(),
      };
      const response = await fetch(ENDPOINTS.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      setLoading(false);
      if (data.status === "login-valido") {
        setLoginUnidade(localStorage.getItem('ultimaUnidade') || "");
        setLoginUsuario(localStorage.getItem('ultimoUsuario') || "");
        setLoginSenha(senha);
        setLoginGrupoId(data.grupo_id || "");
        setLoginGrupoNome(data.grupo_nome || "");
        setLoginLoginId(data.login_id || "");
        setLoginLoginNome(data.login_nome || "");
        setLoginDepartamentoId(data.departamento_id || "");
        // --- PERMISSÕES ---
        let permissoes = data.permissoes;
        console.log("Permissões recebidas do backend:", permissoes, typeof permissoes);
        if (typeof permissoes === "string") {
          try {
            permissoes = JSON.parse(permissoes);
          } catch (e) {
            permissoes = {};
          }
        }
        setPermissoes(permissoes);
        console.log("Permissões setadas no contexto:", permissoes, typeof permissoes);
        setLogado(true);
      } else if (data.status === "login-invalido") {
        setErro(data.motivo || "Usuário ou senha incorretos!");
      } else if (data.status === "unidade inválida") {
        setErro("Unidade inválida!");
      } else {
        setErro("Erro desconhecido no login.");
      }
    } catch (error) {
      console.error("Erro ao fazer login:", error);
      setLoading(false);
      setErro("Erro de conexão com o servidor.");
    }
  }

  if (logado) {
    return <Dashboard usuario={loginLoginNome} unidade={loginUnidade} onLogout={() => setLogado(false)} />;
  }

  // --- INÍCIO: Login automático para links mobile ---
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const unitParam = urlParams.get('unit');
    const osParam = urlParams.get('os');
    const actionParam = urlParams.get('action');
    if (unitParam && osParam && actionParam) {
      // Salva no localStorage para contexto de autenticação
      localStorage.setItem('ultimaUnidade', unitParam);
      localStorage.setItem('ultimoUsuario', 'bot');
      localStorage.setItem('senha', 'bot');
      // Aqui pode ser necessário setar no contexto de autenticação também, se usado
      // Renderiza diretamente a tela mobile
      return <OSMobileView />;
    }
  }
  // --- FIM: Login automático para links mobile ---

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: "radial-gradient(circle at center, #e0c28a 0%, #c44d2d 60%, #232b23 100%)",
      }}
    >
      {loading && <Loader />}
      <style>{`
        .login-anim {
          animation: loginFadeZoom 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes loginFadeZoom {
          0% {
            opacity: 0;
            transform: scale(0.85);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <div className="login-anim flex">
        <div className="bg-[#181e29] rounded-l-lg p-8 flex flex-col justify-center w-[350px] h-[400px] shadow-lg">
          <div className="flex flex-col items-center mb-6">
            <Image
              src="/assets/logo_login.png"
              alt="Logo ADV SERV"
              width={180}
              height={60}
              className="mb-2"
            />
            <span className="text-xs text-[#3ec6e0] tracking-widest mt-1">SERVINDO COM QUALIDADE</span>
          </div>
          <form className="flex flex-col gap-3 flex-1" onSubmit={handleSubmit}>
            <select
              className="rounded px-3 py-2 bg-[#232b23] text-white focus:outline-none"
              value={unidade}
              onChange={e => setUnidade(e.target.value)}
            >
              {unidades.map((unidade) => (
                <option key={unidade} value={unidade}>
                  {unidade}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Usuário"
              value={usuario}
              onChange={e => setUsuario(e.target.value)}
              className="rounded px-3 py-2 bg-[#232b23] text-white focus:outline-none placeholder:text-gray-400"
            />
            <input
              type="password"
              placeholder="Senha"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              className="rounded px-3 py-2 bg-[#232b23] text-white focus:outline-none placeholder:text-gray-400"
            />
            <button
              type="submit"
              className="bg-[#c44d2d] text-white rounded py-2 mt-2 font-semibold hover:bg-[#a53a1a] transition-colors"
              disabled={loading}
            >
              Login
            </button>
          </form>
          <div className="text-xs text-center text-gray-400 mt-6">
            ©2025 Floripa Software<br />
            {versao}
          </div>
        </div>
        <div className="bg-white rounded-r-lg items-center justify-center w-[320px] h-[400px] shadow-lg hidden md:flex">
          {imagemDireita && (
            <Image
              src={imagemDireita}
              alt="Ilustração de atendimento"
              width={220}
              height={220}
              className="object-contain"
            />
          )}
        </div>
      </div>
      <ModalErro open={erro.length > 0} onClose={() => setErro("")} />
    </div>
  );
}

// Função para mostrar label só se a fatia for maior que 5%
function labelIfBigEnough(value: number, context: Context) {
  const dataset = context.dataset.data as number[];
  const total = dataset.reduce((a: number, b: number) => a + b, 0);
  const percent = (value / total) * 100;
  return percent > 5 ? value : '';
}

function useAppVersion() {
  const [versao, setVersao] = useState('v3.0.0');

  useEffect(() => {
    fetch('/version.txt')
      .then(res => res.text())
      .then(txt => setVersao(txt.trim()))
      .catch(() => setVersao('v3.0.0'));
  }, []);

  return versao;
}
